import React, { useState, useRef, useEffect } from 'react';
import { Play, X, ArrowRight } from 'lucide-react';

/**
 * Reusable video-poster + full-screen playback modal.
 *
 * <IntroVideoModal
 *    src="https://.../intro.mp4"
 *    poster="/images/healthcare/hc-portfolio-tracker-hero.jpg"
 *    kicker="90-sec introduction"
 *    title="Watch the introduction"
 *    body="..."
 * />
 */
const IntroVideoModal = ({
  src,
  poster,
  kicker = '90-sec introduction',
  title = 'Watch the introduction',
  body,
  testid = 'intro-video',
}) => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const hasVideo = Boolean(src);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const closeModal = () => {
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (_) { /* ignore */ }
    }
    setOpen(false);
  };

  return (
    <>
      <a
        href="#watch"
        data-testid={`${testid}-poster`}
        onClick={(e) => { e.preventDefault(); if (hasVideo) setOpen(true); }}
        aria-label={title}
        className="group relative block bg-slate-900 rounded-2xl p-2.5 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.45)] ring-1 ring-slate-900/5 overflow-hidden focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/40"
      >
        <div className="relative rounded-lg overflow-hidden">
          <img src={poster} alt="" loading="eager"
               className="w-full aspect-[16/10] object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/55 to-slate-950/25" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white text-[10px] font-semibold uppercase tracking-[0.18em] border border-white/15 rounded-full px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            {kicker}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="relative flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-blue-500/30 blur-2xl group-hover:bg-blue-400/50 transition-colors" />
              <span className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-white text-slate-900 shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Play className="w-8 h-8 md:w-10 md:h-10 ml-1 fill-current" />
              </span>
            </span>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
            <p className="text-white font-medium text-base md:text-lg leading-snug max-w-xl">{title}</p>
            {body && <p className="mt-2 text-[12px] md:text-[13px] text-slate-300 leading-relaxed max-w-xl">{body}</p>}
            <span className="mt-3 inline-flex items-center text-[12px] font-semibold text-blue-300 group-hover:text-blue-200">
              {hasVideo ? 'Play video' : 'Video coming soon'}
              <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </a>

      {open && (
        <div role="dialog" aria-modal="true" data-testid={`${testid}-modal`}
             className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
             onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <button type="button" onClick={closeModal} aria-label="Close video"
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 inline-flex items-center gap-2 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-full pl-4 pr-3 py-2 text-sm font-medium backdrop-blur-sm">
            Close <X className="w-4 h-4" />
          </button>
          <div className="relative w-full max-w-[min(96vw,calc(90vh*16/9))]">
            <video ref={videoRef} src={src} poster={poster} controls autoPlay playsInline
                   className="w-full aspect-video rounded-lg bg-black shadow-2xl"
                   data-testid={`${testid}-player`} onEnded={closeModal} />
          </div>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[11px] text-white/50 uppercase tracking-[0.2em]">
            Press Esc to close
          </p>
        </div>
      )}
    </>
  );
};

export default IntroVideoModal;
